import { useEffect, forwardRef } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';

interface MobileOptimizerProps {
  children: React.ReactNode;
}

export function MobileOptimizer({ children }: MobileOptimizerProps) {
  const isMobile = useIsMobile();

  useEffect(() => {
    // Set viewport for mobile optimization
    const viewport = document.querySelector('meta[name=viewport]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');
    } else {
      const newViewport = document.createElement('meta');
      newViewport.name = 'viewport';
      newViewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';
      document.head.appendChild(newViewport);
    }

    // Add mobile-specific classes to body
    if (isMobile) {
      document.body.classList.add('mobile-optimized');
      
      // Prevent zoom on double tap
      let lastTouchEnd = 0;
      const preventZoom = (e: TouchEvent) => {
        const now = new Date().getTime();
        if (now - lastTouchEnd <= 300) {
          e.preventDefault();
        }
        lastTouchEnd = now;
      };
      
      document.addEventListener('touchend', preventZoom, { passive: false });
      
      return () => {
        document.removeEventListener('touchend', preventZoom);
        document.body.classList.remove('mobile-optimized');
      };
    }
  }, [isMobile]);

  return <>{children}</>;
}

// Mobile-specific component wrappers
export function MobileCard({ children, className = '', ...props }: any) {
  const isMobile = useIsMobile();
  
  return (
    <div 
      className={`${className} ${isMobile ? 'rounded-2xl shadow-sm border-0 mx-2 my-3' : 'rounded-xl shadow-md border'}`}
      {...props}
    >
      {children}
    </div>
  );
}

export const MobileButton = forwardRef<HTMLButtonElement, any>(
  ({ children, className = '', size = 'default', ...props }, ref) => {
    const isMobile = useIsMobile();
    
    const mobileClasses = isMobile 
      ? 'min-h-[48px] px-6 py-3 text-base font-semibold rounded-xl touch-manipulation' 
      : '';
    
    const sizeClasses = isMobile && size === 'sm' 
      ? 'min-h-[40px] px-4 py-2 text-sm rounded-lg' 
      : '';

    return (
      <button 
        ref={ref}
        className={`${className} ${mobileClasses} ${sizeClasses} transition-all duration-150`}
        {...props}
      >
        {children}
      </button>
    );
  }
);

MobileButton.displayName = 'MobileButton';

export function MobileInput({ className = '', ...props }: any) {
  const isMobile = useIsMobile();
  
  return (
    <input 
      className={`${className} ${isMobile ? 'text-base min-h-[48px] px-4 py-3 rounded-xl border-2' : ''}`}
      {...props}
    />
  );
}